import { apiSlice } from "@/services/api-slice";
import { APIResponse } from "@/types/custom-http";
import * as inter from "./certifications.interface";

export const certificationsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCertificationsSimple: builder.query<
      APIResponse<inter.CertificationView[]>,
      { state_id?: number; kind_id?: number; period_start?: string | null; period_end?: string | null } | void
    >({
      query: (params) => ({
        url: "auth/certifications",
        params: params ?? undefined,
      }),
      providesTags: ["Certifications"],
    }),

    getSubmittedCertificationsSimple: builder.query<
      APIResponse<inter.CertificationViewPaginatedData>,
      { search?: string | null; page?: number; size?: number } | void
    >({
      query: (params) => {
        const finalParams: { search?: string; page?: number; size?: number } = {};
        const search = params && "search" in params ? params.search : null;
        const page = params && "page" in params ? params.page : undefined;
        const size = params && "size" in params ? params.size : undefined;
        if (search && search.trim().length > 0) {
          finalParams.search = search.trim();
        }
        if (typeof page === "number" && page > 0) {
          finalParams.page = page;
        }
        if (typeof size === "number" && size > 0) {
          finalParams.size = size;
        }
        return {
          url: "auth/certifications/submitted",
          params: Object.keys(finalParams).length > 0 ? finalParams : undefined,
        };
      },
      providesTags: ["Certifications"],
    }),

    getCertificationsComplete: builder.query<
      APIResponse<inter.CertificationDetails[]>,
      { state_id?: number; kind_id?: number; period_start?: string | null; period_end?: string | null } | void
    >({
      query: (params) => ({
        url: "auth/certifications/full",
        params: params ?? undefined,
      }),
      providesTags: ["Certifications"],
    }),

    getCertificationKinds: builder.query<APIResponse<inter.CertificationKind[]>, void>({
      query: () => ({ url: "auth/certifications/kinds" }),
      providesTags: ["CertificationKinds"],
    }),

    getCertificationStates: builder.query<APIResponse<inter.CertificationState[]>, void>({
      query: () => ({ url: "auth/certifications/states" }),
      providesTags: ["CertificationStates"],
    }),

    createCertification: builder.mutation<APIResponse<inter.CertificationDetails>, inter.CreateCertificationRequest>({
      query: (data) => ({
        url: "auth/certifications",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Certifications", "WorkOrders"],
    }),

    getCertificationById: builder.query<APIResponse<inter.CertificationDetails>, number>({
      query: (id) => ({ url: `auth/certifications/${id}` }),
      providesTags: (_result, _error, id) => [{ type: "Certification", id }],
    }),

    listCertificationsByWorkOrder: builder.query<APIResponse<inter.CertificationView[]>, number>({
      query: (workOrderId) => ({ url: `auth/works-orders/${workOrderId}/certifications` }),
      providesTags: ["Certifications"],
    }),

    updateCertificationDraft: builder.mutation<APIResponse<inter.CertificationDetails>, { id: number; changes: inter.UpdateCertificationRequest }>({
      query: ({ id, changes }) => ({
        url: `auth/certifications/${id}`,
        method: "PATCH",
        body: changes,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Certification", id }, "Certifications", "WorkOrders"],
    }),

    deleteCertificationDraft: builder.mutation<APIResponse<null>, number>({
      query: (id) => ({
        url: `auth/certifications/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Certifications", "WorkOrders"],
    }),

    submitCertification: builder.mutation<APIResponse<null>, { id: number }>({
      query: ({ id }) => ({
        url: `auth/certifications/${id}/submit`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Certification", id }, "Certifications", "WorkOrders"],
    }),

    approveCertification: builder.mutation<APIResponse<null>, { id: number }>({
      query: ({ id }) => ({
        url: `auth/certifications/${id}/approve`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Certification", id }, "Certifications", "WorkOrders"],
    }),

    rejectCertification: builder.mutation<APIResponse<null>, { id: number; body: inter.RejectCertificationRequest }>({
      query: ({ id, body }) => ({
        url: `auth/certifications/${id}/reject`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Certification", id }, "Certifications", "WorkOrders"],
    }),

    voidCertification: builder.mutation<APIResponse<null>, { id: number; body: inter.VoidCertificationRequest }>({
      query: ({ id, body }) => ({
        url: `auth/certifications/${id}/void`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Certification", id }, "Certifications", "WorkOrders"],
    }),
  }),
});

export const {
  useGetCertificationKindsQuery,
  useLazyGetCertificationKindsQuery,
  useGetCertificationStatesQuery,
  useLazyGetCertificationStatesQuery,
  useGetCertificationsSimpleQuery,
  useLazyGetCertificationsSimpleQuery,
  useGetSubmittedCertificationsSimpleQuery,
  useLazyGetSubmittedCertificationsSimpleQuery,
  useGetCertificationsCompleteQuery,
  useLazyGetCertificationsCompleteQuery,
  useCreateCertificationMutation,
  useGetCertificationByIdQuery,
  useLazyGetCertificationByIdQuery,
  useListCertificationsByWorkOrderQuery,
  useLazyListCertificationsByWorkOrderQuery,
  useUpdateCertificationDraftMutation,
  useDeleteCertificationDraftMutation,
  useSubmitCertificationMutation,
  useApproveCertificationMutation,
  useRejectCertificationMutation,
  useVoidCertificationMutation,
} = certificationsApi;
